import { getEntityLabel, type EntityCopyKey } from './entities';

export const contextMessages = {
  'selection.project.continue': 'Select a project to continue.',
  'selection.workspace.continue': 'Select a workspace to continue.',
  'selection.project.manageSettings': 'Select a project to manage settings.',
  'selection.workspace.manageSettings': 'Select a workspace to manage settings.',
  'selection.project.viewDetails': ({ entity }: { entity: EntityCopyKey }) =>
    `Select a project to view ${getEntityLabel(entity)} details.`,
  'selection.project.viewNamedDetails': ({ label }: { label: string }) =>
    `Select a project to view ${label} details.`,
} as const;

export type ContextMessageCatalog = typeof contextMessages;
export type ContextMessageKey = keyof ContextMessageCatalog;

export type ContextMessageArgs<K extends ContextMessageKey> = ContextMessageCatalog[K] extends (
  args: infer TArgs,
) => string
  ? TArgs
  : undefined;

export const getContextMessage = <K extends ContextMessageKey>(
  key: K,
  args?: ContextMessageArgs<K>,
): string => {
  const entry = contextMessages[key];
  if (typeof entry === 'function') {
    return (entry as (messageArgs: ContextMessageArgs<K>) => string)(
      args ?? ({} as ContextMessageArgs<K>),
    );
  }
  return entry;
};

type ContextSampleArgs = {
  [K in ContextMessageKey]?: ContextMessageArgs<K>;
};

const sampleArgs: ContextSampleArgs = {
  'selection.project.viewDetails': { entity: 'executionRun' },
  'selection.project.viewNamedDetails': { label: 'results' },
};

const getContextMessageSample = <K extends ContextMessageKey>(
  key: K,
  args: ContextMessageArgs<K> | undefined,
): { key: ContextMessageKey; value: string } => ({
  key,
  value: getContextMessage(key, args),
});

export const getContextMessageSamples = (): Array<{ key: ContextMessageKey; value: string }> =>
  (Object.keys(contextMessages) as ContextMessageKey[]).map((key) => ({
    key,
    value: getContextMessageSample(key, sampleArgs[key] as never).value,
  }));
